import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Select,
  Button,
  Table as MuiTable,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import IconComponent from "../../components/Icons/IconComponent";
import {
  buildTableRowsPerPageOptions,
  TABLE_BODY_ROW_HEIGHT,
  TABLE_HEADER_FONT_SIZE,
  TABLE_INITIAL_ROWS_PER_PAGE,
} from "../Table/constants.js";

const ROWS_OPTIONS = buildTableRowsPerPageOptions([5, 10, 25, 50, 100]);
const ROW_HEIGHT = Number.parseFloat(String(TABLE_BODY_ROW_HEIGHT)) || 0;
const ROW_HEIGHT_CSS =
  typeof TABLE_BODY_ROW_HEIGHT === "number"
    ? `${TABLE_BODY_ROW_HEIGHT}px`
    : TABLE_BODY_ROW_HEIGHT;

const SELECTION_COLUMN = {
  field: "__selection__",
  label: "",
  width: 48,
  minWidth: 48,
  align: "center",
  resizable: false,
};

const getRowId = (row) => row?.id ?? row?.userId ?? row?.gridId;

const mergeUniqueIds = (ids) => {
  const seen = new Set();
  const result = [];
  ids.forEach((id) => {
    const key = String(id);
    if (seen.has(key)) return;
    seen.add(key);
    result.push(id);
  });
  return result;
};

export default function Table({
  rows = [],
  columns: customColumns,
  loading = false,
  error = null,
  styles,
  checkboxSelection = true,
  rowSelectionModel = [],
  onRowSelectionModelChange,
  onDepositWithdraw,
  onEditUserInfo,
  onSeeTradingAccounts,
  onSeeWalletMovements,
  onExportFullReport,
  onExportSummaryReport,
  onCopyLink,
  onDeleteAccount,
  showDeleteAccount = false,
  isDeleteAccountDisabled,
  // Archive User (Brokers / IBs tabs). Shown only when showArchiveUser is true.
  onArchiveUser,
  showArchiveUser = false,
  // Archived tab: extra columns (Type, Wallet transactions, Pending requests)
  // and the Actions menu only offers Restore / Delete.
  archivedMode = false,
  onRestoreAccount,
  onDeleteArchivedAccount,
  onRowClick,
  onActionMenuOpen,
  actionHighlightRowId = null,
  activeRowId = null,
  highlightActiveRow = false,
  tableMinWidth = 700,
  headerVariant = "default",
  showBrokerCode = false,
  sortField = "userId",
  sortDirection = "asc",
  onSortChange,
  footerLeadingContent = null,
}) {
  const [page, setPage] = useState(0);
  const [isResized, setIsResized] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(TABLE_INITIAL_ROWS_PER_PAGE);
  const [columnWidths, setColumnWidths] = useState({});
  const tableWrapperRef = useRef(null);
  const [tableWrapperWidth, setTableWrapperWidth] = useState(0);

  const totalCount = rows.length;

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / rowsPerPage));
  }, [totalCount, rowsPerPage]);

  useEffect(() => setPage(0), [rowsPerPage]);
  useEffect(() => setPage(0), [totalCount]);
  useEffect(() => {
    if (page > totalPages - 1) setPage(totalPages - 1);
  }, [page, totalPages]);

  useEffect(() => {
    const node = tableWrapperRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;

    const updateWidth = (width) => {
      setTableWrapperWidth((prev) => (Math.abs(prev - width) < 2 ? prev : width));
    };

    updateWidth(node.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const nextWidth = entries?.[0]?.contentRect?.width;
      if (!Number.isFinite(nextWidth)) return;
      updateWidth(nextWidth);
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [page, rows, rowsPerPage]);

  const pageItems = useMemo(() => {
    const last = totalPages;
    const current = page + 1;

    if (last <= 4) return Array.from({ length: last }, (_, index) => index + 1);
    if (current <= 2) return [1, 2, 3, 4, "…"];
    if (current >= last - 1) return ["…", last - 3, last - 2, last - 1, last];

    return ["…", current - 1, current, current + 1, "…"];
  }, [page, totalPages]);

  const arrowBtnSx = (disabled) => ({
    minWidth: 30,
    height: 30,
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E5E7EB",
    color: disabled ? "#9CA3AF" : "#374151",
    boxShadow: "none",
    padding: 0,
    "&:hover": {
      backgroundColor: "#F9FAFB",
      borderColor: "#D1D5DB",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "#FFFFFF",
      borderColor: "#E5E7EB",
      color: "#9CA3AF",
      opacity: 1,
    },
  });

  const ActionsMenuCell = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const deleteDisabled = Boolean(isDeleteAccountDisabled?.(row));

    const handleOpenMenu = (event) => {
      event.stopPropagation();
      onActionMenuOpen?.(row);
      setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = (event) => {
      event?.stopPropagation?.();
      setAnchorEl(null);
    };

    return (
      <>
        <Box sx={styles?.ActionCellWrapper}>
          <IconButton sx={styles?.ActionCircle} onClick={handleOpenMenu}>
            <IconComponent icon="more_vert" size="16px" />
          </IconButton>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {/* Archived tab: only Restore / Delete */}
          {archivedMode && onRestoreAccount && (
            <MenuItem onClick={(e) => { e.stopPropagation(); setAnchorEl(null); onRestoreAccount(row); }}>
              Restore
            </MenuItem>
          )}
          {archivedMode && onDeleteArchivedAccount && (
            <MenuItem
              onClick={(e) => { e.stopPropagation(); setAnchorEl(null); onDeleteArchivedAccount(row); }}
              sx={{ color: "#EB0000", fontWeight: 700 }}
            >
              Delete
            </MenuItem>
          )}

          {/* Brokers / IBs tabs */}
          {!archivedMode && onEditUserInfo && (
            <MenuItem onClick={(e) => { e.stopPropagation(); setAnchorEl(null); onEditUserInfo(row); }}>
              Edit user info
            </MenuItem>
          )}
          {!archivedMode && onSeeWalletMovements && (
            <MenuItem onClick={(e) => { e.stopPropagation(); setAnchorEl(null); onSeeWalletMovements(row); }}>
              See wallet movements
            </MenuItem>
          )}
          {!archivedMode && onDepositWithdraw && (
            <MenuItem onClick={(e) => { e.stopPropagation(); setAnchorEl(null); onDepositWithdraw(row); }}>
              Deposit / Withdraw
            </MenuItem>
          )}
          {!archivedMode && onSeeTradingAccounts && (
            <MenuItem onClick={(e) => { e.stopPropagation(); setAnchorEl(null); onSeeTradingAccounts(row); }}>
              See trading accounts
            </MenuItem>
          )}
          {!archivedMode && onExportFullReport && (
            <MenuItem onClick={(e) => { e.stopPropagation(); setAnchorEl(null); onExportFullReport(row); }}>
              Export full report
            </MenuItem>
          )}
          {!archivedMode && onExportSummaryReport && (
            <MenuItem onClick={(e) => { e.stopPropagation(); setAnchorEl(null); onExportSummaryReport(row); }}>
              Export summary report
            </MenuItem>
          )}
          {!archivedMode && onCopyLink && (
            <MenuItem onClick={(e) => { e.stopPropagation(); setAnchorEl(null); onCopyLink(row); }}>
              Copy Link
            </MenuItem>
          )}
          {/* Archive User: ochre / brown tone, always right above Delete Account */}
          {!archivedMode && showArchiveUser && onArchiveUser && (
            <MenuItem
              onClick={(e) => { e.stopPropagation(); setAnchorEl(null); onArchiveUser(row); }}
              sx={{ color: "#A16207", fontWeight: 700 }}
            >
              Archive User
            </MenuItem>
          )}
          {!archivedMode && showDeleteAccount && onDeleteAccount && (
            <MenuItem
              disabled={deleteDisabled}
              onClick={(e) => {
                e.stopPropagation();
                if (deleteDisabled) return;
                setAnchorEl(null);
                onDeleteAccount(row);
              }}
              sx={{
                color: "#EB0000",
                fontWeight: 700,
                "&.Mui-disabled": {
                  color: "#FCA5A5",
                  opacity: 1,
                },
              }}
            >
              Delete Account
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const defaultColumns = useMemo(
    () => [
      { field: "registration_date", headerName: "Registration", width: 60, minWidth: 60, sortable: true },
      { field: "name", headerName: "Name", width: 180, minWidth: 80, sortable: true },
      { field: "userId", headerName: "ID", width: 100, minWidth: 60, sortable: true },
      { field: "email", headerName: "Email", width: 220, minWidth: 100, sortable: true },
      ...(archivedMode
        ? [{ field: "accountType", headerName: "Type", width: 90, minWidth: 70, sortable: true }]
        : []),
      { field: "walletBalance", headerName: "Wallet balance", width: 150, minWidth: 90, sortable: true },
      ...(archivedMode
        ? [
            { field: "walletTransactions", headerName: "Wallet transactions", width: 150, minWidth: 100, sortable: true },
            { field: "pendingRequest", headerName: "Pending requests", width: 150, minWidth: 100, sortable: true },
          ]
        : []),
      ...(showBrokerCode
        ? [{ field: "brokerCode", headerName: "Broker code", width: 120, minWidth: 80, sortable: true }]
        : []),
      {
        field: "status",
        headerName: "Status",
        width: 100,
        minWidth: 80,
        resizable: false,
        sortable: true,
        renderCell: (params) => {
          const normalizedStatus = String(params.value || "").toUpperCase();
          const isActive = normalizedStatus === "ACTIVE";
          return (
            <span
              style={{
                color: isActive ? "green" : "#EB0000",
                fontWeight: 600,
                background: isActive ? "#e8f5e9" : "#ffebee",
                borderRadius: "12px",
                padding: "2px 8px",
                fontSize: "12px",
              }}
            >
              {isActive ? "Enable" : "Disable"}
            </span>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 80,
        minWidth: 60,
        resizable: false,
        sortable: false,
        align: "center",
        renderCell: (params) => <ActionsMenuCell row={params.row} />,
      },
    ],
    [
      onDepositWithdraw,
      onEditUserInfo,
      onSeeTradingAccounts,
      onSeeWalletMovements,
      onExportFullReport,
      onExportSummaryReport,
      onCopyLink,
      onDeleteAccount,
      showDeleteAccount,
      isDeleteAccountDisabled,
      onArchiveUser,
      showArchiveUser,
      archivedMode,
      onRestoreAccount,
      onDeleteArchivedAccount,
      showBrokerCode,
      styles,
    ]
  );

  const baseColumns = customColumns ?? defaultColumns;

  const handleSortToggle = useCallback(
    (column) => {
      if (column.sortable === false || typeof onSortChange !== "function") return;
      onSortChange(column.field);
    },
    [onSortChange]
  );

  useEffect(() => {
    if (isResized) return;

    const initialWidths = {};
    baseColumns.forEach((column) => {
      initialWidths[column.field] = column.width ?? column.minWidth ?? 120;
    });
    setColumnWidths(initialWidths);
  }, [baseColumns, isResized]);

  const normalizedColumns = useMemo(() => {
    return baseColumns.map((column) => ({
      align: column.align || "left",
      label: column.headerName ?? column.label ?? column.field,
      minWidth: column.minWidth ?? 50,
      width: columnWidths[column.field] ?? column.width ?? column.minWidth ?? 100,
      ...column,
    }));
  }, [baseColumns, columnWidths]);

  const handleColumnResizeStart = useCallback(
    (event, column) => {
      if (column.resizable === false) return;

      event.preventDefault();
      event.stopPropagation();

      const headerRow = event.currentTarget.closest("tr");
      if (headerRow) {
        const currentWidths = {};
        const ths = Array.from(headerRow.querySelectorAll("th"));

        normalizedColumns.forEach((col, idx) => {
          const cellIndex = checkboxSelection ? idx + 1 : idx;
          const th = ths[cellIndex];
          if (th) {
            currentWidths[col.field] = Math.round(th.getBoundingClientRect().width);
          }
        });

        setColumnWidths((prev) => ({ ...prev, ...currentWidths }));
      }

      setIsResized(true);

      const headerCell = event.currentTarget.closest("th");
      const actualWidthOnScreen = headerCell
        ? Math.round(headerCell.getBoundingClientRect().width)
        : columnWidths[column.field] || column.width;

      const startX = event.clientX;
      const startWidth = actualWidthOnScreen;
      const minWidth = column.minWidth ?? 50;

      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const handlePointerMove = (moveEvent) => {
        const deltaX = moveEvent.clientX - startX;
        const nextWidth = Math.max(minWidth, Math.round(startWidth + deltaX));

        setColumnWidths((prev) => ({
          ...prev,
          [column.field]: nextWidth,
        }));
      };

      const handlePointerUp = () => {
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointercancel", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerUp);
    },
    [columnWidths, normalizedColumns, checkboxSelection]
  );

  const selectedIdSet = useMemo(() => {
    return new Set((Array.isArray(rowSelectionModel) ? rowSelectionModel : []).map(String));
  }, [rowSelectionModel]);

  const paginatedRowIds = useMemo(() => {
    return paginatedRows.map(getRowId).filter((id) => id !== undefined && id !== null);
  }, [paginatedRows]);

  const allCurrentPageSelected =
    checkboxSelection &&
    paginatedRowIds.length > 0 &&
    paginatedRowIds.every((id) => selectedIdSet.has(String(id)));

  const someCurrentPageSelected =
    checkboxSelection &&
    paginatedRowIds.some((id) => selectedIdSet.has(String(id))) &&
    !allCurrentPageSelected;

  const handleSelectAllCurrentPage = (event) => {
    event.stopPropagation();
    if (event.target.checked) {
      onRowSelectionModelChange?.(mergeUniqueIds([...rowSelectionModel, ...paginatedRowIds]));
      return;
    }
    const pageIdsSet = new Set(paginatedRowIds.map(String));
    onRowSelectionModelChange?.(rowSelectionModel.filter((id) => !pageIdsSet.has(String(id))));
  };

  const handleRowSelectionToggle = (event, rowId) => {
    event.stopPropagation();
    const rowKey = String(rowId);
    if (selectedIdSet.has(rowKey)) {
      onRowSelectionModelChange?.(rowSelectionModel.filter((selectedId) => String(selectedId) !== rowKey));
      return;
    }
    onRowSelectionModelChange?.([...rowSelectionModel, rowId]);
  };

  const renderCellContent = (row, column) => {
    const value = row?.[column.field] ?? "";
    if (typeof column.renderCell === "function") {
      return column.renderCell({ field: column.field, row, value });
    }
    return value !== null && value !== undefined && value !== "" ? value : "—";
  };

  const headerCellBaseSx = {
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontSize: TABLE_HEADER_FONT_SIZE,
    fontWeight: 700,
    color: "#374151",
    backgroundColor: "#F9F9F9",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    px: 1.5,
    py: 1,
    boxSizing: "border-box",
    position: "relative",
    overflow: "visible",
  };

  const bodyCellBaseSx = {
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontSize: 13,
    color: "#374151",
    borderBottom: "1px solid #E5E7EB",
    backgroundColor: "#FFFFFF",
    height: ROW_HEIGHT_CSS,
    px: 1.5,
    py: 0,
    boxSizing: "border-box",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const renderTable = () => {
    const dataColumnsWidth = normalizedColumns.reduce(
      (acc, col) => acc + (columnWidths[col.field] || col.width || 100),
      0
    );
    const totalPxWidth = dataColumnsWidth + (checkboxSelection ? SELECTION_COLUMN.width : 0);
    const hasOverflow = isResized && totalPxWidth > tableWrapperWidth;

    return (
      <TableContainer
        sx={{
          width: "100%",
          borderRadius: "12px",
          border: "1px solid rgba(0,0,0,0.06)",
          overflowX: hasOverflow ? "auto" : "hidden",
          overflowY: "hidden",
          backgroundColor: "#FFFFFF",
        }}
      >
        <MuiTable
          sx={{
            width: hasOverflow ? `${totalPxWidth}px` : "100%",
            tableLayout: "fixed",
            backgroundColor: "#FFFFFF",
          }}
        >
          <colgroup>
            {checkboxSelection && (
              <col key={SELECTION_COLUMN.field} style={{ width: `${SELECTION_COLUMN.width}px` }} />
            )}
            {normalizedColumns.map((column) => (
              <col
                key={column.field}
                style={
                  isResized
                    ? { width: `${columnWidths[column.field]}px` }
                    : { width: column.width ? `${column.width}px` : "auto" }
                }
              />
            ))}
          </colgroup>

          <TableHead>
            <TableRow>
              {checkboxSelection && (
                <TableCell
                  padding="checkbox"
                  sx={{
                    ...headerCellBaseSx,
                    width: SELECTION_COLUMN.width,
                    textAlign: "center",
                    px: 0,
                  }}
                >
                  <Checkbox
                    size={headerVariant === "brokerTrees" ? "small" : "medium"}
                    checked={allCurrentPageSelected}
                    indeterminate={someCurrentPageSelected}
                    onChange={handleSelectAllCurrentPage}
                    onClick={(event) => event.stopPropagation()}
                    sx={{ p: 0, ...(styles?.GreenCheckbox ?? {}) }}
                  />
                </TableCell>
              )}

              {normalizedColumns.map((column) => {
                const isSortable = column.sortable !== false && typeof onSortChange !== "function";
                const isSortedColumn = isSortable && sortField === column.field;
                const sortIcon = isSortedColumn ? (
                  sortDirection === "asc" ? (
                    <ArrowUpwardIcon sx={{ fontSize: 14, color: "#15202A", flexShrink: 0 }} />
                  ) : (
                    <ArrowDownwardIcon sx={{ fontSize: 14, color: "#15202A", flexShrink: 0 }} />
                  )
                ) : null;

                return (
                  <TableCell
                    key={column.field}
                    align={column.align}
                    onClick={() => handleSortToggle(column)}
                    sx={{
                      ...headerCellBaseSx,
                      cursor: isSortable ? "pointer" : "default",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent:
                          column.align === "center"
                            ? "center"
                            : column.align === "right"
                              ? "flex-end"
                              : "flex-start",
                        gap: 0.5,
                        width: "100%",
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        noWrap
                        component="span"
                        title={column.label}
                        sx={{
                          fontSize: "inherit",
                          fontWeight: "inherit",
                          color: "inherit",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column.label}
                      </Typography>
                      {sortIcon}
                    </Box>

                    {column.resizable !== false && (
                      <Box
                        component="span"
                        role="separator"
                        onPointerDown={(event) => handleColumnResizeStart(event, column)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: -5,
                          width: "10px",
                          height: "100%",
                          cursor: "col-resize",
                          zIndex: 2,
                          touchAction: "none",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "&::after": {
                            content: '""',
                            width: "2px",
                            height: "50%",
                            backgroundColor: "rgba(0, 0, 0, 0.15)",
                            transition: "background-color 0.2s",
                          },
                          "&:hover::after": {
                            backgroundColor: "#15202A",
                          },
                        }}
                      />
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedRows.map((row) => {
              const rowId = getRowId(row);
              const isHighlighted =
                (highlightActiveRow && activeRowId != null && String(rowId) === String(activeRowId)) ||
                (actionHighlightRowId != null && String(rowId) === String(actionHighlightRowId));

              return (
                <TableRow
                  key={rowId}
                  hover
                  onClick={() => onRowClick?.(row)}
                  sx={{
                    cursor: onRowClick ? "pointer" : "default",
                    backgroundColor: isHighlighted ? "#ECF1F7" : "#FFFFFF",
                    "&:hover": { backgroundColor: "#ECF1F7" },
                  }}
                >
                  {checkboxSelection && (
                    <TableCell
                      padding="checkbox"
                      sx={{
                        ...bodyCellBaseSx,
                        width: SELECTION_COLUMN.width,
                        textAlign: "center",
                        backgroundColor: isHighlighted ? "#ECF1F7" : "#FFFFFF",
                        px: 0,
                      }}
                    >
                      <Checkbox
                        checked={selectedIdSet.has(String(rowId))}
                        onChange={(event) => handleRowSelectionToggle(event, rowId)}
                        onClick={(event) => event.stopPropagation()}
                        sx={{ p: 0, ...(styles?.GreenCheckbox ?? {}) }}
                      />
                    </TableCell>
                  )}

                  {normalizedColumns.map((column) => {
                    const content = renderCellContent(row, column);
                    const primitiveContent =
                      typeof content === "string" || typeof content === "number" ? String(content) : "";

                    return (
                      <TableCell
                        key={`${rowId}-${column.field}`}
                        align={column.align}
                        title={primitiveContent || undefined}
                        sx={{
                          ...bodyCellBaseSx,
                          backgroundColor: isHighlighted ? "#ECF1F7" : "#FFFFFF",
                        }}
                      >
                        {content}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </MuiTable>
      </TableContainer>
    );
  };

  return (
    <Box
      ref={tableWrapperRef}
      sx={{
        ...(styles?.MainCard__table ?? {}),
        maxWidth: "100%",
        minWidth: 0,
      }}
    >
      {error && <Typography color="error">{error}</Typography>}

      {loading ? (
        <Box
          sx={{
            minHeight: 25 + ROW_HEIGHT * Math.max(1, rowsPerPage),
            borderRadius: "12px",
            border: "1px solid rgba(0,0,0,0.06)",
            backgroundColor: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : totalCount === 0 ? (
        <Box
          sx={{
            minHeight: 25 + ROW_HEIGHT,
            borderRadius: "12px",
            border: "1px solid rgba(0,0,0,0.06)",
            backgroundColor: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
          }}
        >
          <Typography sx={{ color: "#6B7280", fontSize: 14 }}>
            No rows found.
          </Typography>
        </Box>
      ) : (
        renderTable()
      )}

      <Box
        sx={{
          mt: 1,
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          flexWrap: "wrap",
        }}
      >
        {footerLeadingContent}

        <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
          Total:&nbsp; <b style={{ color: "#111827" }}>{totalCount}</b>
        </Typography>

        <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
          Pages:&nbsp; <b style={{ color: "#111827" }}>{totalPages}</b>
        </Typography>

        <Typography sx={{ fontSize: 13, color: "#6B7280" }}>Rows:</Typography>

        <Select
          size="small"
          value={rowsPerPage}
          onChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          sx={{ height: 32, minWidth: 70, borderRadius: 2, ".MuiSelect-select": { py: 0.5 } }}
          MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 0.5 } } }}
        >
          {ROWS_OPTIONS.map((rowsOption) => (
            <MenuItem key={rowsOption} value={rowsOption}>
              {rowsOption}
            </MenuItem>
          ))}
        </Select>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Button
            size="small"
            disabled={page === 0}
            onClick={() => setPage((prev) => Math.max(0, prev - 1))}
            sx={arrowBtnSx(page === 0)}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
          </Button>

          {pageItems.map((item, index) => {
            if (item === "…") {
              return (
                <Typography key={`dots-${index}`} sx={{ fontSize: 14, color: "#9CA3AF", px: 0.5 }}>
                  …
                </Typography>
              );
            }

            const pageNumber = item;
            const isActivePage = pageNumber === page + 1;

            return (
              <Button
                key={`page-${pageNumber}`}
                size="small"
                onClick={() => setPage(pageNumber - 1)}
                sx={{
                  minWidth: 28,
                  height: 28,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: isActivePage ? 900 : 700,
                  color: isActivePage ? "#111827" : "#6B7280",
                  backgroundColor: isActivePage ? "#EEF2FF" : "transparent",
                  "&:hover": {
                    backgroundColor: isActivePage ? "#EEF2FF" : "#F3F4F6",
                  },
                }}
              >
                {pageNumber}
              </Button>
            );
          })}

          <Button
            size="small"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
            sx={arrowBtnSx(page >= totalPages - 1)}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
          </Button>
        </Box>
      </Box>
    </Box>
  );
}